/**
 * Sorts report objects by a supported sort key.
 * Returns a sorted shallow copy (does not mutate the input array).
 *
 * Supported sort keys (standard reports):
 * - most_viewed, most_saved, alphabetical_asc, alphabetical_desc, recently_saved
 *
 * Supported sort keys (smart cards):
 * - most_viewed (by userClickRate), needs_attention, ben_insights_pick, newest (by tableauCreatedAt/scId), recently_saved
 *
 * @param {Array} items - Array of report objects
 * @param {string} sortKey - One of the supported sort keys
 * @param {{ newestKeys?: string[], smart?: boolean }} [options]
 * @returns {Array} Sorted shallow copy of items
 */
export function sortReports(items, sortKey, options = {}) {
  if (!Array.isArray(items)) return [];

  const copy = items.slice();
  const isSmart = options?.smart === true;

  const getFirstNumber = (item, keys) => {
    for (const key of keys) {
      const value = item?.[key];
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
      }
    }
    return 0;
  };

  const toDateMs = (value) => {
    if (value == null) return 0;
    if (typeof value === 'number' && Number.isFinite(value)) {
      if (value > 1e12) return value;
      if (value > 1e9) return value * 1000;
      return 0;
    }
    const ms = Date.parse(String(value));
    return Number.isNaN(ms) ? 0 : ms;
  };

  const alphaAsc = (a, b) => (a?.displayText ?? '').localeCompare(b?.displayText ?? '', undefined, { sensitivity: 'base' });
  const alphaDesc = (a, b) => (b?.displayText ?? '').localeCompare(a?.displayText ?? '', undefined, { sensitivity: 'base' });

  const rankAsc = (a, b, keys) => {
    const av = getFirstNumber(a, keys);
    const bv = getFirstNumber(b, keys);
    if (av && bv && av !== bv) return av - bv;
    if ((av || bv) && av !== bv) return av ? -1 : 1;
    return 0;
  };

  const scIdDesc = (a, b) => {
    const aNum = parseInt(String(a?.scId || '').replace(/\D/g, ''), 10) || 0;
    const bNum = parseInt(String(b?.scId || '').replace(/\D/g, ''), 10) || 0;
    return bNum - aNum;
  };

  switch (sortKey) {
    case 'most_viewed':
      if (isSmart) {
        const sorted = copy.sort((a, b) => {
          const rateDiff = getFirstNumber(b, ['userClickRate']) - getFirstNumber(a, ['userClickRate']);
          if (rateDiff !== 0) return rateDiff;
          const viewDiff = getFirstNumber(b, ['viewedCount']) - getFirstNumber(a, ['viewedCount']);
          if (viewDiff !== 0) return viewDiff;
          const r = rankAsc(a, b, ['globalOrder']);
          if (r !== 0) return r;
          return alphaAsc(a, b);
        });
        return sorted;
      }
      return copy.sort((a, b) => {
        const diff = getFirstNumber(b, ['viewedScore', 'viewedCount']) - getFirstNumber(a, ['viewedScore', 'viewedCount']);
        if (diff !== 0) return diff;
        const ao = getFirstNumber(a, ['order']);
        const bo = getFirstNumber(b, ['order']);
        if (ao !== bo) return ao - bo;
        return alphaAsc(a, b);
      });

    case 'most_saved':
      return copy.sort((a, b) => {
        const diff = getFirstNumber(b, ['favoriteCount']) - getFirstNumber(a, ['favoriteCount']);
        if (diff !== 0) return diff;
        return alphaAsc(a, b);
      });

    case 'needs_attention': {
      const sorted = copy.sort((a, b) => {
        const r = rankAsc(a, b, ['rankTrend']);
        if (r !== 0) return r;
        const diff = getFirstNumber(b, ['viewedCount']) - getFirstNumber(a, ['viewedCount']);
        if (diff !== 0) return diff;
        return alphaAsc(a, b);
      });
      return sorted;
    }

    case 'ben_insights_pick': {
      const sorted = copy.sort((a, b) => {
        const r = rankAsc(a, b, ['globalOrder']);
        if (r !== 0) return r;
        return alphaAsc(a, b);
      });
      return sorted;
    }

    case 'newest':
      if (isSmart) {
        const sorted = copy.sort((a, b) => {
          const bd = toDateMs(b?.tableauCreatedAt);
          const ad = toDateMs(a?.tableauCreatedAt);
          if (bd !== ad) return bd - ad;
          const diff = scIdDesc(a, b);
          if (diff !== 0) return diff;
          return alphaAsc(a, b);
        });
        return sorted;
      }
      return copy.sort(alphaAsc);

    case 'recently_saved': {
      const savedKeys = Array.isArray(options?.newestKeys) && options.newestKeys.length ? options.newestKeys : ['createdDttm'];
      return copy.sort((a, b) => {
        const bd = Math.max(...savedKeys.map((key) => toDateMs(b?.[key])));
        const ad = Math.max(...savedKeys.map((key) => toDateMs(a?.[key])));
        if (bd !== ad) return bd - ad;
        return alphaAsc(a, b);
      });
    }

    case 'alphabetical_desc':
      return copy.sort(alphaDesc);

    case 'alphabetical_asc':
    default:
      return copy.sort(alphaAsc);
  }
}
